import { useEffect, useRef, useCallback, useMemo } from 'react'
import { SESSION_IDLE_TIMEOUT_MS } from '@Constants'

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
]

/** Throttle: invoke fn at most once every waitMs. */
const throttle = (fn, waitMs) => {
  let last = 0
  return function throttled(...args) {
    const now = Date.now()
    if (now - last >= waitMs) {
      last = now
      fn.apply(this, args)
    }
  }
}

const THROTTLE_MS = 1000

/**
 * Invalidates session after a period of user inactivity (idle timeout).
 * Only runs when user is logged in. On timeout, dispatches LOGOUT_USER_DATA
 * and optionally redirects to login.
 * Activity handlers are throttled (at most once per second) to avoid perf impact.
 *
 * @param {Object} options
 * @param {boolean} options.isLogin - Whether the user is currently logged in
 * @param {Function} options.dispatch - User context dispatch (for LOGOUT_USER_DATA)
 * @param {Function} [options.onTimeout] - Called when idle timeout fires (e.g. redirect to /login)
 */
export const useIdleSessionTimeout = ({
  isLogin,
  dispatch,
  onTimeout,
}) => {
  const timerRef = useRef(null)
  const onTimeoutRef = useRef(onTimeout)
  onTimeoutRef.current = onTimeout

  const resetTimer = useCallback(() => {
    if (!isLogin) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      dispatch({ type: 'LOGOUT_USER_DATA', payload: {} })
      if (typeof onTimeoutRef.current === 'function') {
        onTimeoutRef.current()
      }
    }, SESSION_IDLE_TIMEOUT_MS)
  }, [isLogin, dispatch])

  const resetTimerRef = useRef(resetTimer)
  resetTimerRef.current = resetTimer

  const throttledResetTimer = useMemo(
    () => throttle(() => resetTimerRef.current(), THROTTLE_MS),
    []
  )

  useEffect(() => {
    if (!isLogin) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    resetTimer()

    const handleActivity = () => throttledResetTimer()

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isLogin, resetTimer, throttledResetTimer])

  return { resetTimer }
}

export default useIdleSessionTimeout
